import React, { useState, useEffect } from 'react';
import styles from '../Users/UsersManagement.module.css';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash,
  UserPlus,
  Users as UsersIcon,
  Shield,
  User,
  Phone,
  Mail,
  Home,
  Building,
  Filter,
  MapPin,
  Loader,
  Settings,
  X
} from 'lucide-react';
import {
  getListUsers,
  removeManagerRole
} from '../../Fetch/fetchUser';
import { authenticatedFetchWithRefresh } from '../../utils/enhancedTokenManager';
import SimpleUserForm from './SimpleUserForm';

// User Details Modal Component
const UserDetailsModal = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  const getRoleDisplay = (user) => {
    const roles = [];
    if (user.IsManager === "כן") roles.push('מנהל');
    if (user.IsVaad === "כן") roles.push('חבר ועד');
    if (user.IsTenant === "כן") roles.push('דייר');
    return roles.length > 0 ? roles.join(', ') : 'לא מוגדר';
  };

  const getRoleBadgeClass = (user) => {
    if (user.IsManager === "כן") return styles.roleAdmin;
    if (user.IsVaad === "כן") return styles.roleCommittee;
    if (user.IsTenant === "כן") return styles.roleResident;
    return styles.roleDefault;
  };

  return (
    <div className={styles.userModalOverlay} onClick={onClose}>
      <div className={styles.userModalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.userModalHeader}>
          <div className={styles.userHeaderInfo}>
            <div className={styles.userAvatar}>
              <User size={32} />
            </div>
            <div className={styles.userHeaderText}>
              <h2 className={styles.userName}>{user.FullName}</h2>
              <span className={`${styles.userRole} ${getRoleBadgeClass(user)}`}>
                {getRoleDisplay(user)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className={styles.userModalCloseButton}
            aria-label="סגור"
          >
            ×
          </button>
        </div>
        
        <div className={styles.userModalBody}>
          <div className={styles.detailSection}>
            <h3 className={styles.sectionTitle}>
              <Mail size={20} />
              פרטי התקשרות
            </h3>
            <div className={styles.sectionContent}>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Mail size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>דוא"ל</span>
                  <span className={styles.detailValue}>{user.Email || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Phone size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>טלפון</span>
                  <span className={styles.detailValue}>{user.Phone || 'לא צוין'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.detailSection}>
            <h3 className={styles.sectionTitle}>
              <Home size={20} />
              פרטי הדירה
            </h3>
            <div className={styles.sectionContent}>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Home size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>מספר דירה</span>
                  <span className={styles.detailValue}>{user.ApartmentNumber || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Building size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>קומה</span>
                  <span className={styles.detailValue}>{user.FloorNumber || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <MapPin size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>סכום חודשי</span>
                  <span className={styles.detailValueHighlight}>₪{user.VaadMonthly || 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.detailSection}>
            <h3 className={styles.sectionTitle}>
              <Settings size={20} />
              מידע מערכת
            </h3>
            <div className={styles.sectionContent}>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <User size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>מזהה משתמש</span>
                  <span className={styles.detailValueMuted}>{user.UserID || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <Home size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>מזהה דירה</span>
                  <span className={styles.detailValueMuted}>{user.ApartmentID || 'לא צוין'}</span>
                </div>
              </div>
              <div className={styles.detailRow}>
                <div className={styles.detailIcon}>
                  <MapPin size={16} />
                </div>
                <div className={styles.detailInfo}>
                  <span className={styles.detailLabel}>עיר</span>
                  <span className={styles.detailValue}>{user.City || 'לא צוין'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.userModalFooter}>
          <button
            onClick={onClose}
            className={styles.userCloseButton}
          >
            <Settings size={16} />
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

// Manager Card Component for Mobile View
const ManagerCard = ({ user, buildingId, onEdit, onRemoveManager, getRoleDisplay, getRoleColor }) => {
  return (
    <div className={styles.dataCard}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <div className={styles.cardIcon}>
            <User size={18} />
          </div>
          <span>{user.FullName}</span>
        </div>
        <span className={`${styles.roleBadge} ${getRoleColor(user)}`}>
          {getRoleDisplay(user)}
        </span>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardInfo}>
          <Mail size={16} />
          <span>{user.Email}</span>
        </div>

        <div className={styles.cardInfo}>
          <Phone size={16} />
          <span>{user.Phone}</span>
        </div>

        <div className={styles.cardInfo}>
          <Building size={16} />
          <span>קומה: {user.FloorNumber || 'לא צוין'}</span>
        </div>

        <div className={styles.cardInfo}>
          <Home size={16} />
          <span>דירה: {user.ApartmentNumber || 'לא צוין'}</span>
        </div>

        <div className={styles.cardActions}>
          <button
            className={styles.editButton}
            onClick={() => onEdit(user)}
            title="עריכה"
            aria-label={`עריכת ${user.FullName}`}
          >
            <Edit size={18} />
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => onRemoveManager(user)}
            title="הסר הרשאות ניהול"
            aria-label={`הסר הרשאות ניהול מ${user.FullName}`}
          >
            <Shield size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, title, value, color = "primary" }) => {
  return (
    <div className={styles.statCard}>
      <div className={styles.statIcon} style={{ color: `var(--${color}-color)` }}>
        <Icon size={24} />
      </div>
      <div>
        <h3>{title}</h3>
        <p className={styles.statValue}>{value}</p>
      </div>
    </div>
  );
};

const Managers = () => {
  const [managers, setManagers] = useState([]);
  const [filteredManagers, setFilteredManagers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [managersPerPage, setManagersPerPage] = useState(10);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // User details modal state
  const [userDetailsModal, setUserDetailsModal] = useState({
    isOpen: false,
    user: null
  });

  // Statistics
  const totalManagers = managers.length;
  const managersWithVaad = managers.filter(m => m.IsVaad === 'כן').length;
  const managersOnlyRole = managers.filter(m => m.IsVaad !== 'כן' && m.IsTenant !== 'כן').length;

  // Get building ID helper
  const getBuildingId = () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    return userData?.buildingId;
  };

  // Load managers data
  useEffect(() => {
    loadManagers();
  }, []);

  // Add event listener for building changes
  useEffect(() => {
    const handleBuildingChange = () => {
      loadManagers();
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    window.addEventListener('userDataChanged', handleBuildingChange);

    return () => {
      window.removeEventListener('buildingChanged', handleBuildingChange);
      window.removeEventListener('userDataChanged', handleBuildingChange);
    };
  }, []);

  const loadManagers = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user'));
      const buildingId = userData?.buildingId;

      if (!buildingId) {
        setManagers([]);
        return;
      }

      const data = await getListUsers(buildingId);

      // Filter ONLY managers (IsManager = כן) - exclude users who also have isVaad or isTenant roles
      const managersData = data.filter(user => {
        const isManager = user.isManager === 'כן' || user.IsManager === 'כן';
        const isVaad = user.isVaad === 'כן' || user.IsVaad === 'כן';
        const isTenant = user.isTenant === 'כן' || user.IsTenant === 'כן';
        
        // Only show users who are ONLY managers (no other roles)
        return isManager && !isVaad && !isTenant;
      });

      const transformedData = managersData.map(user => ({
        UserID: user.userID || user.UserID,
        FullName: user.fullName || user.FullName,
        Email: user.email || user.Email,
        Phone: user.phone || user.Phone,
        PasswordHash: user.passwordHash || user.PasswordHash || '',
        IsVaad: user.isVaad || user.IsVaad || false,
        IsTenant: user.isTenant || user.IsTenant || false,
        IsManager: user.isManager || user.IsManager || false,
        RelationshipType: user.relationshipType || user.RelationshipType || 'שוכר',
        ApartmentNumber: user.apartmentNumber || user.ApartmentNumber || 'לא צוין',
        FloorNumber: user.floorNumber || user.FloorNumber || 'לא צוין',
        VaadMonthly: user.vaadMonthly || user.VaadMonthly || 0,
        ApartmentID: user.apartmentID || user.ApartmentID || null,
        BuildingId: user.buildingID || user.BuildingID || buildingId,
        City: user.city || user.City || 'תל אביב'
      }));

      setManagers(transformedData);
      setSearchTerm('');
      setCurrentPage(1);

    } catch (error) {
      setManagers([]);
      alert('שגיאה בטעינת המשתמשים: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter managers
  useEffect(() => {
    let filtered = managers;

    if (searchTerm) {
      filtered = filtered.filter(manager => {
        const searchLower = searchTerm.toLowerCase();
        const apartmentNumber = manager.ApartmentNumber ?
          String(manager.ApartmentNumber).toLowerCase() : '';
        const floorNumber = manager.FloorNumber ?
          String(manager.FloorNumber).toLowerCase() : '';

        return (
          manager.FullName?.toLowerCase().includes(searchLower) ||
          manager.Email?.toLowerCase().includes(searchLower) ||
          manager.Phone?.includes(searchTerm) ||
          apartmentNumber.includes(searchLower) ||
          floorNumber.includes(searchLower)
        );
      });
    }

    setFilteredManagers(filtered);
    setCurrentPage(1);
  }, [searchTerm, managers]);

  // Clear filters function
  const clearFilters = () => {
    setSearchTerm('');
  };

  // Pagination
  const indexOfLastManager = currentPage * managersPerPage;
  const indexOfFirstManager = indexOfLastManager - managersPerPage;
  const currentManagers = filteredManagers.slice(indexOfFirstManager, indexOfLastManager);
  const totalPages = Math.ceil(filteredManagers.length / managersPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAddManager = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleRemoveManagerRole = async (user) => {
    if (window.confirm(`האם אתה בטוח שברצונך להסיר את ${user.FullName} מהבניין לחלוטין? (פעולה זו תסיר אותם מכל הדירות ומהבניין)`)) {
      try {
        console.log('🚀 Calling RemoveUserFromBuilding API...');
        console.log('User:', user.UserID, 'Building:', getBuildingId());
        
        // Call the new API endpoint that completely removes the user from the building
        const response = await authenticatedFetchWithRefresh(`${process.env.REACT_APP_API_URL}/api/Users/RemoveUserFromBuilding`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            UserID: user.UserID, 
            BuildingID: getBuildingId() 
          })
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (response.ok) {
          const result = await response.json();
          console.log('✅ API Response:', result);
          alert(result.message || 'המשתמש הוסר מהבניין בהצלחה');
          await loadManagers();
        } else {
          const errorData = await response.json();
          console.error('❌ API Error:', errorData);
          throw new Error(errorData.message || 'שגיאה בהסרת המשתמש');
        }
      } catch (error) {
        console.error('❌ Error removing user from building:', error);
        alert(`שגיאה: ${error.message}`);
      }
    }
  };

  // Handle row click to show user details
  const handleRowClick = (user) => {
    setUserDetailsModal({
      isOpen: true,
      user: user
    });
  };

  const handleUserSaved = async (savedUser) => {
    try {
      const action = editingUser ? 'עודכן' : 'נוצר';
      setShowForm(false);
      setEditingUser(null);
      await loadManagers();
      alert(`משתמש ${action} בהצלחה`);
    } catch (error) {
      setShowForm(false);
      setEditingUser(null);
      alert('המשתמש נשמר בהצלחה, אך יש בעיה ברענון הרשימה. אנא רענן את הדף.');
    }
  };

  const getRoleDisplay = (user) => {
    const roles = [];
    if (user.IsManager === "כן") roles.push('מנהל');
    if (user.IsVaad === "כן") roles.push('חבר ועד');
    if (user.IsTenant === "כן") roles.push('דייר');
    return roles.length > 0 ? roles.join(', ') : 'לא מוגדר';
  };

  const getRoleColor = (user) => {
    if (user.IsManager) return styles.roleAdmin;
    if (user.IsVaad) return styles.roleCommittee;
    if (user.IsTenant) return styles.roleResident;
    return styles.roleResident;
  };

  // Show SimpleUserForm if form is active
  if (showForm) {
    return (
      <SimpleUserForm
        onSave={handleUserSaved}
        onCancel={() => setShowForm(false)}
        forceManager={true}
      />
    );
  }

  return (
    <div className={styles.usersManagement}>
      <div className={styles.header}>
        <div>
          <h1>משתמשים - חברת ניהול</h1>
        </div>
        <div className={styles.stats}>
          <StatCard
            icon={Shield}
            title="סה״כ מנהלים"
            value={totalManagers}
            color="primary"
          />
        </div>
      </div>

      <div className={styles.actions}>
        <div className={styles.filtersContainer}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder="חיפוש מנהלים..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="חיפוש מנהלים"
            />
            <Search size={20} className={styles.searchIcon} />
          </div>

          {searchTerm && (
            <button
              className={styles.clearFiltersButton}
              onClick={clearFilters}
              title="נקה מסננים"
              aria-label="נקה כל המסננים"
            >
              <Filter size={18} />
              נקה
            </button>
          )}
        </div>

        <div className={styles.buttonsGroup}>
          <button
            className={styles.addButton}
            onClick={handleAddManager}
            aria-label="הוסף מנהל חדש"
          >
            <UserPlus size={20} />
            הוסף מנהל חדש
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingContainer} role="status" aria-live="polite">
          <div className={styles.loadingSpinner} aria-hidden="true"></div>
          <p>טוען מנהלים...</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className={styles.tableContainer}>
            <table className={styles.usersTable} role="table">
              <thead>
                <tr role="row">
                  <th role="columnheader">שם</th>
                  <th role="columnheader">טלפון</th>
                  <th role="columnheader">קומה</th>
                  <th role="columnheader">דירה</th>
                  <th role="columnheader">הרשאות משתמש</th>
                  <th role="columnheader">פעולות</th>
                </tr>
              </thead>
              <tbody>
                {currentManagers.length > 0 ? (
                  currentManagers.map((user, index) => (
                    <tr 
                      key={`${user.UserID}-${user.Email}-${index}`} 
                      role="row"
                      onClick={() => handleRowClick(user)}
                      className={styles.clickableRow}
                      title="לחץ לצפייה בפרטים"
                    >
                      <td className={styles.userName}>{user.FullName}</td>
                      <td>{user.Phone}</td>
                      <td>{user.FloorNumber || 'לא צוין'}</td>
                      <td>{user.ApartmentNumber || 'לא צוין'}</td>
                      <td>
                        <span className={`${styles.roleBadge} ${getRoleColor(user)}`}>
                          {getRoleDisplay(user)}
                        </span>
                      </td>
                      <td className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.actionButtons}>
                          <button
                            className={styles.editButton}
                            onClick={() => handleEditUser(user)}
                            title="עריכה"
                            aria-label={`עריכת ${user.FullName}`}
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleRemoveManagerRole(user)}
                            title="הסר הרשאות ניהול"
                            aria-label={`הסר הרשאות ניהול מ${user.FullName}`}
                          >
                            <Shield size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className={styles.noResults}>
                      {searchTerm ? 'לא נמצאו מנהלים התואמים לחיפוש' : 'אין מנהלים בבניין זה'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className={styles.mobileCardsContainer}>
            <div className={styles.cardsGrid} role="grid">
              {currentManagers.length > 0 ? (
                currentManagers.map((user, index) => (
                  <ManagerCard
                    key={`${user.UserID}-${user.Email}-${index}`}
                    user={user}
                    buildingId={getBuildingId()}
                    onEdit={handleEditUser}
                    onRemoveManager={handleRemoveManagerRole}
                    getRoleDisplay={getRoleDisplay}
                    getRoleColor={getRoleColor}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  {searchTerm ? 'לא נמצאו מנהלים התואמים לחיפוש' : 'אין מנהלים בבניין זה'}
                </div>
              )}
            </div>
          </div>

          {filteredManagers.length > 0 && (
            <div className={styles.pagination} role="navigation" aria-label="ניווט בין עמודים">
              <div className={styles.paginationControls}>
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={styles.paginationButton}
                  aria-label="עמוד קודם"
                >
                  <ChevronRight size={20} />
                </button>
                <span className={styles.pageInfo} aria-live="polite">
                  עמוד {currentPage} מתוך {totalPages}
                </span>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={styles.paginationButton}
                  aria-label="עמוד הבא"
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className={styles.resultsPerPage}>
                <label htmlFor="results-per-page">תוצאות בעמוד:</label>
                <select
                  id="results-per-page"
                  value={managersPerPage}
                  onChange={(e) => {
                    setManagersPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
              <div className={styles.totalInfo} aria-live="polite">
                מציג {indexOfFirstManager + 1}-{Math.min(indexOfLastManager, filteredManagers.length)} מתוך {filteredManagers.length} מנהלים
              </div>
            </div>
          )}
        </>
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={userDetailsModal.isOpen}
        onClose={() => setUserDetailsModal({ isOpen: false, user: null })}
        user={userDetailsModal.user}
      />
    </div>
  );
};

export default Managers;